export interface ProvisionFormValues {
  profileId: string;
  realName: string;
  username: string;
  loginPassword: string;
  loginPasswordConfirmation: string;
  wifiSsid: string;
  wifiPassword: string;
  encryptDisk: boolean;
  useLoginPasswordForLuks: boolean;
  luksPassword: string;
  luksPasswordConfirmation: string;
  sshPublicKey: string;
  installCodecs: boolean;
  confirmErase: boolean;
}

export type ProvisionField = keyof ProvisionFormValues;
export type ProvisionErrors = Partial<Record<ProvisionField, string>>;

const usernamePattern = /^[a-z_][a-z0-9_-]{0,31}$/;
const publicKeyPattern = /^(ssh-ed25519|sk-ssh-ed25519@openssh\.com|ssh-rsa)\s+[A-Za-z0-9+/=]+(?:\s+.*)?$/;

function utf8Length(value: string): number {
  return new TextEncoder().encode(value).length;
}

function realNameIsValid(value: string): boolean {
  return value.trim().length >= 2;
}

function usernameIsValid(value: string): boolean {
  return usernamePattern.test(value);
}

function loginPasswordIsValid(values: ProvisionFormValues): boolean {
  return (
    values.loginPassword.length >= 12 &&
    values.loginPassword === values.loginPasswordConfirmation
  );
}

function wifiSsidIsValid(value: string): boolean {
  const length = utf8Length(value);

  return length >= 1 && length <= 32;
}

function wifiPasswordIsValid(value: string): boolean {
  return /^[a-fA-F0-9]{64}$/.test(value) || (value.length >= 8 && value.length <= 63);
}

function luksPasswordIsValid(values: ProvisionFormValues): boolean {
  if (!values.encryptDisk) {
    return true;
  }

  if (values.useLoginPasswordForLuks) {
    return loginPasswordIsValid(values);
  }

  return (
    values.luksPassword.length >= 12 &&
    values.luksPassword === values.luksPasswordConfirmation
  );
}

export function resolveLuksPassword(values: ProvisionFormValues): string | null {
  if (!values.encryptDisk) {
    return null;
  }

  return values.useLoginPasswordForLuks ? values.loginPassword : values.luksPassword;
}

function sshPublicKeyIsValid(value: string): boolean {
  return publicKeyPattern.test(value.trim());
}

export function validateProvisionForm(values: ProvisionFormValues): ProvisionErrors {
  const errors: ProvisionErrors = {};

  if (!realNameIsValid(values.realName)) {
    errors.realName = 'Informe o nome exibido na tela de login.';
  }

  if (!usernameIsValid(values.username)) {
    errors.username = 'Use até 32 caracteres: letras minúsculas, números, _ ou -.';
  }

  if (values.loginPassword.length < 12) {
    errors.loginPassword = 'Use pelo menos 12 caracteres.';
  }

  if (values.loginPassword !== values.loginPasswordConfirmation) {
    errors.loginPasswordConfirmation = 'As senhas de login não coincidem.';
  }

  if (!wifiSsidIsValid(values.wifiSsid)) {
    errors.wifiSsid = 'O nome da rede deve ter entre 1 e 32 bytes.';
  }

  if (!wifiPasswordIsValid(values.wifiPassword)) {
    errors.wifiPassword = 'Use de 8 a 63 caracteres, ou uma PSK hexadecimal de 64.';
  }

  if (values.encryptDisk && !values.useLoginPasswordForLuks) {
    if (values.luksPassword.length < 12) {
      errors.luksPassword = 'Use pelo menos 12 caracteres para criptografar o disco.';
    }

    if (values.luksPassword !== values.luksPasswordConfirmation) {
      errors.luksPasswordConfirmation = 'As passphrases do disco não coincidem.';
    }
  }

  if (!sshPublicKeyIsValid(values.sshPublicKey)) {
    errors.sshPublicKey = 'Informe uma chave pública SSH válida.';
  }

  if (!values.confirmErase) {
    errors.confirmErase = 'Confirme o apagamento do NVMe interno.';
  }

  return errors;
}

export function formIsValid(errors: ProvisionErrors): boolean {
  return Object.keys(errors).length === 0;
}

export function readinessCount(values: ProvisionFormValues): number {
  return [
    realNameIsValid(values.realName) && usernameIsValid(values.username),
    loginPasswordIsValid(values),
    wifiSsidIsValid(values.wifiSsid) && wifiPasswordIsValid(values.wifiPassword),
    luksPasswordIsValid(values),
    sshPublicKeyIsValid(values.sshPublicKey) && values.confirmErase,
  ].filter(Boolean).length;
}
